import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import LeaderboardTable from '../components/leaderboard/LeaderboardTable';
import { getLeaderboard, getUserRank } from '../apiClient';
import './LeaderboardPage.css';

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLeaderboard = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const data = await getLeaderboard({ page, limit: 100 });
      setLeaderboardData(data);

      // Calculate total pages
      const pages = Math.ceil(data.total / 100);
      setTotalPages(pages);
      setCurrentPage(page);

      // Fetch user's personal rank if authenticated
      if (user) {
        try {
          const rankData = await getUserRank();
          setUserRank(rankData);
        } catch (rankError) {
          console.log('Could not fetch user rank:', rankError.message);
          setUserRank(null);
        }
      }

    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError(err.message || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard(1);
  }, [user]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      fetchLeaderboard(page);
    }
  };

  const handleRefresh = () => {
    fetchLeaderboard(currentPage);
  };

  if (loading) {
    return (
      <div className="leaderboard-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-page">
        <div className="error-container">
          <h2>Error Loading Leaderboard</h2>
          <p>{error}</p>
          <button onClick={handleRefresh} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-header">
        <h1>Leaderboard</h1>
        <p>See how you rank against other learners</p>

        {/* User's personal rank display */}
        {userRank && (
          <div className="user-rank-card">
            <h3>Your Ranking</h3>
            {userRank.is_visible ? (
              <div className="rank-info">
                <span className="rank-number">#{userRank.rank}</span>
                <span className="tasks-count">{userRank.tasks_solved} tasks solved</span>
                {userRank.percentile && (
                  <span className="percentile">Top {Math.round(100 - userRank.percentile)}%</span>
                )}
              </div>
            ) : (
              <div className="rank-info hidden">
                <span className="tasks-count">{userRank.tasks_solved} tasks solved</span>
                <span className="hidden-status">Not shown publicly</span>
              </div>
            )}
          </div>
        )}

        {!user && (
          <div className="auth-prompt">
            <p>Sign in to see your personal ranking!</p>
          </div>
        )}
      </div>

      {/* Leaderboard table */}
      {leaderboardData && leaderboardData.entries && leaderboardData.entries.length > 0 ? (
        <LeaderboardTable
          entries={leaderboardData.entries}
          currentUser={user}
          onRefresh={handleRefresh}
        />
      ) : (
        <div className="empty-leaderboard">
          <h3>No users on leaderboard yet</h3>
          <p>Be the first to solve an exercise and appear on the leaderboard!</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="page-button"
          >
            First
          </button>

          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-button"
          >
            Previous
          </button>

          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="page-button"
          >
            Next
          </button>

          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="page-button"
          >
            Last
          </button>
        </div>
      )}

      {/* Refresh button */}
      <div className="leaderboard-actions">
        <button onClick={handleRefresh} className="refresh-button">
          Refresh Leaderboard
        </button>
      </div>
    </div>
  );
};

export default LeaderboardPage;